import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const SettingsContext = createContext(null);

// Darken a hex color by a 0..1 ratio. Returns hex string. Used to derive the
// "primary-dark" hover shade from the chosen primary color.
function darken(hex, ratio = 0.12) {
  try {
    const h = (hex || "").replace("#", "");
    if (h.length !== 6) return hex;
    const r = Math.max(0, Math.round(parseInt(h.slice(0, 2), 16) * (1 - ratio)));
    const g = Math.max(0, Math.round(parseInt(h.slice(2, 4), 16) * (1 - ratio)));
    const b = Math.max(0, Math.round(parseInt(h.slice(4, 6), 16) * (1 - ratio)));
    return "#" + [r, g, b].map(v => v.toString(16).padStart(2, "0")).join("");
  } catch { return hex; }
}

const FALLBACK = {
  brand_name: "FlightAggregator",
  tagline: "Explore. Book. Travel.",
  primary_color: "#14b8a6",
  secondary_color: "#86efac",
  accent_color: "#134e4a",
  hero_image: "https://images.pexels.com/photos/10708651/pexels-photo-10708651.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600",
  hero_title: "Where will your next adventure take you?",
  hero_subtitle: "Flights, hotels, car rentals, holiday packages and unforgettable experiences — all bookable in one seamless flow.",
  footer_powered_by: "Powered by Skyxhub Technologies LLC",
  contact_email: "hello@flightaggregator.com",
  contact_phone: "+1 (800) 555-0199",
  contact_address: "350 Skyway Ave, NY",
  product_flight: true, product_hotel: true, product_car: true, product_package: true, product_sightseeing: true,
};

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(FALLBACK);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/site/settings");
      setSettings({ ...FALLBACK, ...data });
    } catch (_e) {}
  }, []);

  useEffect(() => { load(); }, [load]);

  // Inject CSS custom props from settings for runtime theming
  useEffect(() => {
    const root = document.documentElement;
    const primary = settings.primary_color || FALLBACK.primary_color;
    root.style.setProperty("--brand-primary", primary);
    root.style.setProperty("--brand-secondary", settings.secondary_color || FALLBACK.secondary_color);
    root.style.setProperty("--brand-accent", settings.accent_color || FALLBACK.accent_color);
    root.style.setProperty("--brand-primary-dark", darken(primary, 0.12));
  }, [settings.primary_color, settings.secondary_color, settings.accent_color]);

  return <SettingsContext.Provider value={{ settings, reload: load, setSettings }}>{children}</SettingsContext.Provider>;
}

export const useSettings = () => useContext(SettingsContext) || { settings: FALLBACK, reload: () => {}, setSettings: () => {} };
